import { EVENTS, SELECTORS, ARIA, CLASS_NAMES, MISC } from '@bs/constants';

/**
 * Lightweight modal controller specialized for video modals.
 * Usage:
 *  - Modal markup element has class `modal` and an `id`.
 *  - Add `data-loadcomponent="VideoModal"` to the modal root.
 *  - Any trigger with `data-modal-open="#modal-id"` opens it.
 *  - Close via `[data-modal-close]` or `.modal__closebutton`, ESC, or overlay click.
 */
export default class VideoModal {
  /**
   * @param {HTMLElement} element Modal root element
   */
  constructor(element) {
    this.modal = element;
    this.closeBtn = this.modal.querySelector(SELECTORS.MODAL_CLOSE_BUTTON);
    this.isOpen = false;
    this.previouslyFocusedElement = null;

    this.onDocClick = this.onDocClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onModalClick = this.onModalClick.bind(this);

    this.init();
  }

  init() {
    this.modal.setAttribute('aria-hidden', 'true');
    document.addEventListener(EVENTS.CLICK, this.onDocClick, false);
    document.addEventListener('keydown', this.onKeyDown, false);
    this.modal.addEventListener(EVENTS.CLICK, this.onModalClick, false);

    if (this.closeBtn) {
      this.closeBtn.addEventListener(EVENTS.CLICK, () => this.close());
    }
  }

  onDocClick(e) {
    if (!e || !(e.target instanceof Element)) return;
    const trigger = e.target.closest('[data-modal-open]');
    if (!trigger) return;
    const target = trigger.getAttribute('data-modal-open');
    if (target && target.replace('#', '') === this.modal.id) {
      e.preventDefault();
      this.open();
    }
  }

  onKeyDown(e) {
    // Handle Enter key on modal triggers
    if (e.key === 'Enter' && !this.isOpen) {
      if (!(e.target instanceof Element)) return;
      const trigger = e.target.closest('[data-modal-open]');
      if (trigger) {
        const target = trigger.getAttribute('data-modal-open');
        if (target && target.replace('#', '') === this.modal.id) {
          e.preventDefault();
          this.open();
          return;
        }
      }
    }

    // Handle Escape key when modal is open
    if (this.isOpen && e.key === 'Escape') {
      this.close();
      return;
    }

    // Focus trap when modal is open
    if (this.isOpen && e.key === 'Tab') {
      const focusableSelectors = [
        'a[href]',
        'area[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'iframe',
        '[tabindex]:not([tabindex="-1"])',
        '[contenteditable="true"]'
      ].join(',');

      const focusable = Array.from(
        this.modal.querySelectorAll(focusableSelectors)
      ).filter((el) => {
        const style = window.getComputedStyle(el);
        const isHidden =
          style.visibility === 'hidden' ||
          style.display === 'none' ||
          el.getAttribute('aria-hidden') === 'true';
        return !isHidden && !el.hasAttribute('disabled');
      });

      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }

      const active = this.modal.contains(document.activeElement)
        ? document.activeElement
        : null;
      const currentIndex = active ? focusable.indexOf(active) : -1;

      e.preventDefault();
      if (e.shiftKey) {
        const prevIndex =
          currentIndex <= 0 ? focusable.length - 1 : currentIndex - 1;
        focusable[prevIndex].focus();
      } else {
        const nextIndex =
          currentIndex === -1 || currentIndex === focusable.length - 1
            ? 0
            : currentIndex + 1;
        focusable[nextIndex].focus();
      }
    }
  }

  onModalClick(e) {
    // Close if clicking on backdrop (root modal element)
    if (e && e.target === this.modal) {
      this.close();
    }
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    this.previouslyFocusedElement = document.activeElement;
    this.modal.classList.add('is-active');
    this.modal.setAttribute('aria-hidden', 'false');

    // Notify listeners that a modal has opened to begin autoplay
    const ev = new CustomEvent(EVENTS.MODAL_OPEN, { detail: this.modal.id });
    document.dispatchEvent(ev);

    // After autoplay kicks in, try to focus the video embed; fallback to first focusable
    setTimeout(() => {
      const embedPrefer = this.modal.querySelector(
        '.ta-ytvideo__embed[tabindex="0"], .ta-ytvideo__embed, iframe'
      );
      if (embedPrefer && typeof embedPrefer.focus === 'function') {
        try {
          embedPrefer.focus();
          return;
        } catch (err) {
          // fall through to fallback
        }
      }
      const first = this.modal.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (first) first.focus();
    }, 0);
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.modal.classList.remove('is-active');
    this.modal.setAttribute('aria-hidden', 'true');

    // Attempt to pause the video inside the modal via custom event
    const yt = this.modal.querySelector('[data-url]');
    if (yt) {
      const url = yt.getAttribute('data-url') || '';
      const id = this.constructor.extractYoutubeId(url);
      if (id) {
        const ev = new CustomEvent(EVENTS.PAUSE_VIDEO, { detail: id });
        document.dispatchEvent(ev);
      }

      // Reset UI state so subsequent opens can autoplay
      const root = yt.closest('.ta-ytvideo');
      if (root) {
        root.classList.remove(CLASS_NAMES.PLAYING);
        const poster = root.querySelector('.ta-ytvideo__poster');
        if (poster) {
          poster.classList.remove(CLASS_NAMES.CLICK);
          poster.setAttribute(ARIA.HIDDEN, 'false');
        }
        const embed = root.querySelector('.ta-ytvideo__embed');
        if (embed) {
          embed.setAttribute(MISC.TABINDEX, '-1');
        }
      }
    }

    // Restore focus to the element that opened the modal
    if (
      this.previouslyFocusedElement &&
      typeof this.previouslyFocusedElement.focus === 'function'
    ) {
      try {
        this.previouslyFocusedElement.focus();
      } catch (err) {
        // ignore
      }
    }
    this.previouslyFocusedElement = null;
  }

  // Minimal YT ID extraction to match VideoYoutube utils behavior
  static extractYoutubeId(url) {
    if (!url) return '';
    const patterns = [
      /(?:v=|\/videos\/|embed\/|youtu\.be\/|\/v\/)([\w-]{11})/,
      /([\w-]{11})$/
    ];
    for (let i = 0; i < patterns.length; i += 1) {
      const m = url.match(patterns[i]);
      if (m && m[1]) return m[1];
    }
    return '';
  }
}
